'use client'
import { logError as _ulogError } from '@/lib/logging/core'
import { useState, useEffect, useCallback, useRef, type CSSProperties } from 'react'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { useTranslations } from 'next-intl'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import TaskStatusInline from '@/components/task/TaskStatusInline'
import { resolveTaskPresentationState } from '@/lib/task/presentation'
import { AppIcon } from '@/components/ui/icons'
import { shouldGuideToModelSetup } from '@/lib/workspace/model-setup'
import { Link, useRouter } from '@/i18n/navigation'
import { apiFetch } from '@/lib/api-fetch'
import { toDisplayImageUrl } from '@/lib/media/image-url'
import { queryKeys } from '@/lib/query/keys'
import { useShortDramaAccess } from '@/lib/short-drama/access-client'
import type { ImageTextProjectDTO } from '@/types/image-text'
import WorkspaceProjectKindTabs from './components/WorkspaceProjectKindTabs'

const ConfirmDialog = dynamic(() => import('@/components/ConfirmDialog'), { ssr: false })

const WORKSPACE_DATE_FORMATTER = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Shanghai',
})

interface ProjectStats {
  episodes: number
  images: number
  videos: number
  panels: number
  firstEpisodePreview: string | null
  firstFrameUrl: string | null
}

interface Project {
  id: string
  name: string
  description: string | null
  createdAt: string
  updatedAt: string
  lastEpisodeId?: string | null
  totalCost?: number  // 项目总费用（CNY）
  stats?: ProjectStats
}

interface Pagination {
  page: number
  pageSize: number
  total: number
  totalPages: number
}

interface WorkspaceProjectsResponse {
  projects: Project[]
  pagination: Pagination
}

interface ImageTextProjectsResponse {
  projects: ImageTextProjectDTO[]
  pagination: Pagination
}

interface AiChatWorkspaceProjectItem {
  id: string
  kind: 'quick-video' | 'image-text' | 'lite-ppt'
  projectId: string
  name: string
  href: string
  previewUrl: string | null
  summary: string | null
  updatedAt: string
  createdAt: string
  status: string | null
  stats: {
    images: number
    videos: number
    pages: number
  }
}

interface AiChatWorkspaceProject {
  id: string
  sessionId: string | null
  name: string
  summary: string | null
  previewUrl: string | null
  createdAt: string
  updatedAt: string
  items: AiChatWorkspaceProjectItem[]
  counts: {
    quickVideos: number
    imageTexts: number
    litePpts: number
    images: number
    videos: number
  }
}

interface AiChatWorkspaceProjectsResponse {
  projects: AiChatWorkspaceProject[]
  pagination: Pagination
}

const PAGE_SIZE = 7
const IMAGE_TEXT_PAGE_SIZE = 7
const WORKSPACE_SECONDARY_COUNTS_IDLE_TIMEOUT_MS = 1200
const WORKSPACE_AI_CHAT_PREVIEW_IDLE_TIMEOUT_MS = 900
const PROJECT_KIND_PARAM = 'type'
const WORKSPACE_SECONDARY_PARAM = 'section'
const WORKSPACE_IMAGE_PREVIEW_EXT_RE = /\.(?:png|jpe?g|webp|gif|bmp|tiff?|heic)(?:$|[?#])/i
const WORKSPACE_VIDEO_PREVIEW_EXT_RE = /\.(?:mp4|webm|mov|m4v|avi|mkv)(?:$|[?#])/i
// `quick-video` is kept as the stable URL/storage value for the merged AI chat works tab.
// User-facing copy presents it as AI对话作品, and `image-text` is normalized into this tab.
const PROJECT_KINDS = ['short-drama', 'quick-video', 'image-text'] as const
const WORKSPACE_SECONDARY_SECTIONS = ['works', 'asset-hub'] as const
type ProjectKind = typeof PROJECT_KINDS[number]
type WorkspaceRouteKind = ProjectKind | 'lite-ppt'
type NovelProjectKind = Exclude<ProjectKind, 'image-text'>
type WorkspaceSecondarySection = typeof WORKSPACE_SECONDARY_SECTIONS[number]

interface DeleteProjectTarget {
  id: string
  name: string
  kind: ProjectKind | 'ai-chat-works'
  items?: AiChatWorkspaceProjectItem[]
}
const EMPTY_PROJECTS: Project[] = []
const DEFAULT_BILLING_CURRENCY = 'CNY'
const WORKSPACE_CREATE_PROJECT_EVENT = 'workspace:create-project'
const LAST_WORKSPACE_HREF_KEY_PREFIX = 'aimanju:last-workspace-href'
const LAST_WORKSPACE_TYPE_HREF_KEY_PREFIX = 'aimanju:last-workspace-type-href'
const LAST_WORKSPACE_TYPE_KEY = 'aimanju:last-workspace-type'
const WORKSPACE_HREF_CHANGED_EVENT = 'workspace:last-href-changed'
const LAST_WORKSPACE_SESSION_HREF_KEY = `${LAST_WORKSPACE_HREF_KEY_PREFIX}:session`
const LAST_WORKSPACE_TYPE_SESSION_HREF_KEY_PREFIX = `${LAST_WORKSPACE_TYPE_HREF_KEY_PREFIX}:session`
const AssetHubContent = dynamic<{ embedded?: boolean }>(
  () => import('./asset-hub/AssetHubContent').then((mod) => mod.AssetHubContent),
  { ssr: false },
)

type WorkspaceIdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number
  cancelIdleCallback?: (handle: number) => void
}

function scheduleWorkspaceIdleTask(callback: () => void, timeoutMs: number) {
  if (typeof window === 'undefined') return () => undefined
  const idleWindow = window as WorkspaceIdleWindow
  if (typeof idleWindow.requestIdleCallback === 'function') {
    const handle = idleWindow.requestIdleCallback(callback, { timeout: timeoutMs })
    return () => idleWindow.cancelIdleCallback?.(handle)
  }
  const timer = window.setTimeout(callback, Math.min(timeoutMs, 300))
  return () => window.clearTimeout(timer)
}

function getLastWorkspaceHrefKey(userId: string) {
  return `${LAST_WORKSPACE_HREF_KEY_PREFIX}:${userId}`
}

function getLastWorkspaceTypeHrefKey(userId: string, kind: ProjectKind) {
  return `${LAST_WORKSPACE_TYPE_HREF_KEY_PREFIX}:${userId}:${kind}`
}

function getLastWorkspaceTypeSessionHrefKey(kind: ProjectKind) {
  return `${LAST_WORKSPACE_TYPE_SESSION_HREF_KEY_PREFIX}:${kind}`
}

function getProjectIdFromWorkspaceHref(href: string | null | undefined) {
  if (!href) return null
  const [pathnamePart] = href.split('?')
  const segments = pathnamePart.split('/').filter(Boolean)
  const workspaceIndex = segments.indexOf('workspace')
  if (workspaceIndex < 0) return null
  const workspaceSegment = segments[workspaceIndex + 1]
  if (!workspaceSegment || workspaceSegment === 'asset-hub' || workspaceSegment === 'ai-square') return null
  if (workspaceSegment === 'ai-chat') return null
  if (workspaceSegment === 'ai-chat-projects') return null
  if (workspaceSegment === 'image-text') return segments[workspaceIndex + 2] || null
  if (workspaceSegment === 'lite-ppt') return segments[workspaceIndex + 2] || null
  return workspaceSegment
}

function getProjectKindFromWorkspaceHref(href: string | null | undefined): WorkspaceRouteKind | null {
  if (!href) return null
  const [pathnamePart] = href.split('?')
  const segments = pathnamePart.split('/').filter(Boolean)
  const workspaceIndex = segments.indexOf('workspace')
  if (workspaceIndex < 0) return null

  const workspaceSegment = segments[workspaceIndex + 1]
  if (!workspaceSegment || workspaceSegment === 'asset-hub' || workspaceSegment === 'ai-square' || workspaceSegment === 'ai-chat') return null
  if (workspaceSegment === 'ai-chat-projects') return 'quick-video'
  if (workspaceSegment === 'image-text') return 'quick-video'
  if (workspaceSegment === 'lite-ppt') return 'lite-ppt'

  return segments[workspaceIndex + 2] === 'quick-video' ? 'quick-video' : 'short-drama'
}

function buildProjectWorkspaceHref(project: Project) {
  if (!project.lastEpisodeId) {
    return `/workspace/${project.id}`
  }

  return `/workspace/${project.id}?episode=${project.lastEpisodeId}`
}

function buildQuickVideoGalleryHref(project: Project) {
  const query = project.lastEpisodeId ? `?episode=${encodeURIComponent(project.lastEpisodeId)}` : ''
  return `/workspace/${project.id}/quick-video${query}`
}

function buildImageTextGalleryHref(project: ImageTextProjectDTO) {
  return `/workspace/image-text/${project.projectId}`
}

function buildAiChatWorkspaceProjectHref(project: AiChatWorkspaceProject) {
  return `/workspace/ai-chat-projects/${encodeURIComponent(project.id)}`
}

function getWorkspacePreviewMediaKind(input: string | null | undefined): 'image' | 'video' | null {
  if (!input) return null
  const raw = input.trim()
  if (!raw) return null
  if (raw.startsWith('data:image/') || raw.startsWith('blob:')) return 'image'
  if (raw.startsWith('data:video/')) return 'video'

  try {
    const parsed = new URL(raw, 'http://localhost')
    const storageKey = parsed.searchParams.get('key') || parsed.searchParams.get('url') || ''
    const haystack = `${parsed.pathname}\n${storageKey}`
    if (WORKSPACE_IMAGE_PREVIEW_EXT_RE.test(haystack)) return 'image'
    if (parsed.pathname.startsWith('/api/quick-video/') || WORKSPACE_VIDEO_PREVIEW_EXT_RE.test(haystack)) return 'video'
  } catch {
    if (WORKSPACE_IMAGE_PREVIEW_EXT_RE.test(raw)) return 'image'
    if (WORKSPACE_VIDEO_PREVIEW_EXT_RE.test(raw)) return 'video'
  }

  return null
}

function buildAiChatWorkspaceDeleteEndpoint(item: AiChatWorkspaceProjectItem) {
  if (item.kind === 'image-text') return `/api/image-text/projects/${item.projectId}`
  if (item.kind === 'lite-ppt') return `/api/lite-ppt/projects/${item.projectId}`
  return `/api/projects/${item.projectId}`
}

function isProjectKind(value: string | null): value is ProjectKind {
  return value === 'short-drama' || value === 'quick-video' || value === 'image-text'
}

function normalizeWorkspaceProjectKind(kind: ProjectKind): ProjectKind {
  return kind === 'image-text' ? 'quick-video' : kind
}

function isWorkspaceSecondarySection(value: string | null): value is WorkspaceSecondarySection {
  return !!value && WORKSPACE_SECONDARY_SECTIONS.includes(value as WorkspaceSecondarySection)
}

function emitWorkspaceHrefChanged(href: string | null) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(WORKSPACE_HREF_CHANGED_EVENT, { detail: { href } }))
}

function formatProjectCost(amount: number, currency = DEFAULT_BILLING_CURRENCY): string {
  if (currency === 'USD') return `$${amount.toFixed(2)}`
  return `¥${amount.toFixed(2)}`
}

function formatWorkspaceCardSummary(value: string | null | undefined, maxLength = 10) {
  const normalized = value?.trim()
  if (!normalized) return ''
  const chars = Array.from(normalized)
  return chars.length > maxLength ? `${chars.slice(0, maxLength).join('')}…` : normalized
}

function clampPreviewRatio(value: number | null) {
  if (!value || !Number.isFinite(value) || value <= 0) return 16 / 9
  return Math.min(2.35, Math.max(0.72, value))
}

const WORKSPACE_PREVIEW_RADIUS = '1.5rem'
const WORKSPACE_PREVIEW_INNER_RADIUS = '1.25rem'
const WORKSPACE_CARD_PREVIEW_HEIGHT_CLASS = 'h-[132px] sm:h-[156px] xl:h-[164px]'
const WORKSPACE_CARD_COPY_WIDTH_CLASS = 'w-[164px] sm:w-[192px] xl:w-[204px]'
const WORKSPACE_PREVIEW_FRAME_BACKGROUND_CLASS = 'bg-[linear-gradient(135deg,#3d5cff_0%,#6c74ff_42%,#8d5eff_100%)]'

function WorkspaceImageRatioPreview({
  src,
  alt = '',
  onRatioChange,
}: {
  src: string
  alt?: string
  onRatioChange?: (ratio: number) => void
}) {
  const [imageRatio, setImageRatio] = useState<number | null>(null)
  const ratio = clampPreviewRatio(imageRatio)
  const frameStyle: CSSProperties = {
    aspectRatio: `${ratio} / 1`,
    borderRadius: WORKSPACE_PREVIEW_RADIUS,
    width: '100%',
  }
  const mediaStyle: CSSProperties = {
    borderRadius: WORKSPACE_PREVIEW_INNER_RADIUS,
  }

  return (
    <div
      className="workspace-image-ratio-shell mx-auto border border-white/60 bg-white/40 p-[3px]"
      data-preview-ratio={imageRatio ? ratio.toFixed(2) : 'pending'}
      style={frameStyle}
    >
      <div
        className="workspace-image-ratio-preview h-full w-full overflow-hidden bg-[linear-gradient(135deg,#eef4ff_0%,#f8fbff_100%)]"
        style={mediaStyle}
      >
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget
            if (naturalWidth > 0 && naturalHeight > 0) {
              const nextRatio = naturalWidth / naturalHeight
              setImageRatio(nextRatio)
              onRatioChange?.(nextRatio)
            }
          }}
        />
      </div>
    </div>
  )
}

function WorkspaceImageRatioCover({
  src,
  alt = '',
  ratio: ratioHint = null,
  isMirrored = false,
  onRatioChange,
}: {
  src: string
  alt?: string
  ratio?: number | null
  isMirrored?: boolean
  onRatioChange?: (ratio: number) => void
}) {
  const [imageRatio, setImageRatio] = useState<number | null>(ratioHint)
  const ratio = clampPreviewRatio(imageRatio)

  useEffect(() => {
    if (ratioHint && Number.isFinite(ratioHint) && ratioHint > 0) {
      setImageRatio(ratioHint)
    }
  }, [ratioHint])

  return (
    <div
      className="workspace-image-ratio-cover h-full w-full"
      data-cover-preview-ratio={imageRatio ? ratio.toFixed(2) : 'pending'}
    >
      <div
        className={`workspace-image-ratio-cover-frame h-full w-full overflow-hidden ${isMirrored ? 'rounded-l-none rounded-r-[1.5rem]' : 'rounded-l-[1.5rem] rounded-r-none'} ${WORKSPACE_PREVIEW_FRAME_BACKGROUND_CLASS}`}
      >
        <img
          src={src}
          alt={alt}
          className="workspace-image-ratio-cover-media h-full w-full object-cover"
          loading="lazy"
          decoding="async"
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget
            if (naturalWidth > 0 && naturalHeight > 0) {
              const nextRatio = naturalWidth / naturalHeight
              setImageRatio(nextRatio)
              onRatioChange?.(nextRatio)
            }
          }}
        />
      </div>
    </div>
  )
}

function WorkspaceProjectPreview({ src }: { src: string }) {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
  const ratio = 16 / 9
  const frameStyle: CSSProperties = {
    aspectRatio: `${ratio} / 1`,
    borderRadius: WORKSPACE_PREVIEW_RADIUS,
  }
  const mediaStyle: CSSProperties = {
    borderRadius: WORKSPACE_PREVIEW_INNER_RADIUS,
  }

  const armVideo = useCallback(() => {
    setShouldLoadVideo(true)
  }, [])

  return (
    <div
      className="workspace-video-ratio-shell mx-auto w-full border border-white/60 bg-white/40 p-[3px]"
      data-preview-ratio={ratio.toFixed(2)}
      style={frameStyle}
      onMouseEnter={armVideo}
      onFocus={armVideo}
    >
      <div
        className="h-full w-full overflow-hidden rounded-2xl border border-white/55 bg-[linear-gradient(135deg,#eef4ff_0%,#f8fbff_100%)]"
        style={mediaStyle}
      >
        {shouldLoadVideo ? (
          <video
            src={src}
            className="h-full w-full object-cover"
            muted
            preload="metadata"
            playsInline
            disablePictureInPicture
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[var(--glass-text-tertiary)]">
            <AppIcon name="video" className="h-7 w-7 opacity-70" />
          </div>
        )}
      </div>
    </div>
  )
}

function WorkspaceProjectPreviewCover({ src, isMirrored = false }: { src: string; isMirrored?: boolean }) {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)

  const armVideo = useCallback(() => {
    setShouldLoadVideo(true)
  }, [])

  return (
    <div
      className="workspace-video-ratio-cover h-full w-full"
      onMouseEnter={armVideo}
      onFocus={armVideo}
    >
      <div className={`h-full w-full overflow-hidden ${isMirrored ? 'rounded-l-none rounded-r-[1.5rem]' : 'rounded-l-[1.5rem] rounded-r-none'} ${WORKSPACE_PREVIEW_FRAME_BACKGROUND_CLASS}`}>
        {shouldLoadVideo ? (
          <video
            src={src}
            className="h-full w-full object-cover"
            muted
            preload="metadata"
            playsInline
            disablePictureInPicture
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-white">
            <AppIcon name="video" className="h-7 w-7 opacity-80" />
          </div>
        )}
      </div>
    </div>
  )
}

async function fetchWorkspaceProjects(
  page: number,
  pageSize: number,
  search: string,
  projectKind: NovelProjectKind,
  countOnly = false,
  includePreview = true,
): Promise<WorkspaceProjectsResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
    type: projectKind,
  })
  if (countOnly) params.set('countOnly', '1')
  if (!includePreview) params.set('preview', '0')
  if (search.trim()) {
    params.set('search', search.trim())
  }
  const response = await apiFetch(`/api/projects?${params}`)
  if (!response.ok) {
    throw new Error('Failed to fetch projects')
  }
  return response.json()
}

async function fetchImageTextProjects(
  page: number,
  pageSize: number,
  search: string,
  countOnly = false,
): Promise<ImageTextProjectsResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  })
  if (countOnly) params.set('countOnly', '1')
  if (search.trim()) {
    params.set('search', search.trim())
  }
  const response = await apiFetch(`/api/image-text/projects?${params}`)
  if (!response.ok) {
    throw new Error('Failed to fetch image text projects')
  }
  return response.json()
}

async function fetchAiChatWorkspaceProjects(
  page: number,
  pageSize: number,
  search: string,
  includePreview = true,
  countOnly = false,
): Promise<AiChatWorkspaceProjectsResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  })
  if (!includePreview) {
    params.set('preview', '0')
  }
  if (countOnly) {
    params.set('countOnly', '1')
  }
  if (search.trim()) {
    params.set('search', search.trim())
  }
  const response = await apiFetch(`/api/workspace/ai-chat-projects?${params}`)
  if (!response.ok) {
    throw new Error('Failed to fetch AI chat workspace projects')
  }
  return response.json()
}

function isDeleteSuccessResponse(response: Response) {
  return response.ok || response.status === 404
}

export default function WorkspacePage() {
  const { data: session, status } = useSession()
  const queryClient = useQueryClient()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  })
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: ''
  })
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null)
  const [batchDeleting, setBatchDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<DeleteProjectTarget | null>(null)
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false)
  const [isBatchSelectMode, setIsBatchSelectMode] = useState(false)
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set())
  const [batchDeleteProgress, setBatchDeleteProgress] = useState<{ completed: number, total: number } | null>(null)

  // 分页和搜索状态
  const [currentPage, setCurrentPage] = useState(1)
  const [imageTextPage, setImageTextPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [modelNotConfigured, setModelNotConfigured] = useState(false)
  const [workspacePreviewRatios, setWorkspacePreviewRatios] = useState<Record<string, number>>({})
  const [currentWorkspaceProjectIds, setCurrentWorkspaceProjectIds] = useState<Record<ProjectKind, string | null>>({
    'short-drama': null,
    'quick-video': null,
    'image-text': null,
  })
  const [secondaryCountsReady, setSecondaryCountsReady] = useState(false)
  const [aiChatPreviewsReady, setAiChatPreviewsReady] = useState(false)
  const createIntentHandledRef = useRef(false)
  const emptyWorkspacePromptHandledRef = useRef(false)
  const userPreferencePayloadRef = useRef<unknown | undefined>(undefined)

  const t = useTranslations('workspace')
  const tc = useTranslations('common')
  const tNav = useTranslations('nav')
  const tImageText = useTranslations('imageText')
  const userId = (session?.user as { id?: string } | undefined)?.id ?? null
  const isAdmin = (session?.user?.name ?? '').trim().toLowerCase() === 'admin'
  const { enabled: shortDramaAccessEnabled, loading: shortDramaAccessLoading } = useShortDramaAccess(true)

  const rememberWorkspacePreviewRatio = useCallback((projectId: string, ratio: number) => {
    setWorkspacePreviewRatios((current) => {
      if (Math.abs((current[projectId] ?? 0) - ratio) < 0.01) return current
      return { ...current, [projectId]: ratio }
    })
  }, [])
  const projectKindFromUrl = searchParams?.get(PROJECT_KIND_PARAM) ?? null
  const workspaceSectionFromUrl = searchParams?.get(WORKSPACE_SECONDARY_PARAM) ?? null
  const initialProjectKind = isProjectKind(projectKindFromUrl)
    ? normalizeWorkspaceProjectKind(projectKindFromUrl)
    : (() => {
      if (typeof window === 'undefined') return 'short-drama'
      const saved = localStorage.getItem(LAST_WORKSPACE_TYPE_KEY)
      return isProjectKind(saved) ? normalizeWorkspaceProjectKind(saved) : 'short-drama'
    })()
  const [activeProjectKind, setActiveProjectKindState] = useState<ProjectKind>(
    initialProjectKind,
  )
  const [activeSecondarySection, setActiveSecondarySectionState] = useState<WorkspaceSecondarySection>(
    initialProjectKind === 'short-drama' && isWorkspaceSecondarySection(workspaceSectionFromUrl)
      ? workspaceSectionFromUrl
      : 'works',
  )

  useEffect(() => {
    if (!isProjectKind(projectKindFromUrl)) return
    const nextKind = normalizeWorkspaceProjectKind(projectKindFromUrl)
    if (nextKind === 'short-drama' && !shortDramaAccessEnabled) {
      setActiveProjectKindState('quick-video')
      return
    }
    setActiveProjectKindState(nextKind)
    if (typeof window !== 'undefined') {
      if (localStorage.getItem(LAST_WORKSPACE_TYPE_KEY) !== nextKind) localStorage.setItem(LAST_WORKSPACE_TYPE_KEY, nextKind)
      if (nextKind !== projectKindFromUrl) {
        const url = new URL(window.location.href)
        url.searchParams.set(PROJECT_KIND_PARAM, nextKind)
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
      }
    }
  }, [projectKindFromUrl, shortDramaAccessEnabled])

  useEffect(() => {
    if (!isAdmin || activeProjectKind !== 'short-drama' || !isWorkspaceSecondarySection(workspaceSectionFromUrl)) {
      setActiveSecondarySectionState('works')
      return
    }
    setActiveSecondarySectionState(workspaceSectionFromUrl)
  }, [activeProjectKind, isAdmin, workspaceSectionFromUrl])

  const setActiveProjectKind = useCallback((kind: ProjectKind) => {
    const requestedKind = normalizeWorkspaceProjectKind(kind)
    const nextKind = requestedKind === 'short-drama' && !shortDramaAccessEnabled ? 'quick-video' : requestedKind
    setActiveProjectKindState(nextKind)
    setActiveSecondarySectionState('works')
    if (typeof window !== 'undefined' && localStorage.getItem(LAST_WORKSPACE_TYPE_KEY) !== nextKind) localStorage.setItem(LAST_WORKSPACE_TYPE_KEY, nextKind)
    setCurrentPage(1)
    setImageTextPage(1)
    setIsBatchSelectMode(false)
    setSelectedProjectIds(new Set())
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.searchParams.set(PROJECT_KIND_PARAM, nextKind)
    url.searchParams.delete(WORKSPACE_SECONDARY_PARAM)
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  }, [shortDramaAccessEnabled])

  useEffect(() => {
    if (shortDramaAccessLoading || shortDramaAccessEnabled || activeProjectKind !== 'short-drama') return
    setActiveProjectKind('quick-video')
  }, [activeProjectKind, setActiveProjectKind, shortDramaAccessEnabled, shortDramaAccessLoading])

  const setActiveSecondarySection = useCallback((section: WorkspaceSecondarySection) => {
    const nextSection = isAdmin && activeProjectKind === 'short-drama' ? section : 'works'
    setActiveSecondarySectionState(nextSection)
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (nextSection === 'asset-hub') {
      url.searchParams.set(PROJECT_KIND_PARAM, 'short-drama')
      url.searchParams.set(WORKSPACE_SECONDARY_PARAM, nextSection)
    } else {
      url.searchParams.delete(WORKSPACE_SECONDARY_PARAM)
    }
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  }, [activeProjectKind, isAdmin])

  const trimmedSearchQuery = searchQuery.trim()

  useEffect(() => {
    setSecondaryCountsReady(false)
  }, [activeProjectKind, trimmedSearchQuery])

  useEffect(() => {
    setAiChatPreviewsReady(false)
  }, [activeProjectKind, currentPage, trimmedSearchQuery])

  const projectListQuery = useQuery({
    queryKey: queryKeys.workspaceProjects.list(currentPage, PAGE_SIZE, trimmedSearchQuery, 'short-drama'),
    enabled: !!session && shortDramaAccessEnabled && activeProjectKind === 'short-drama',
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: () => fetchWorkspaceProjects(currentPage, PAGE_SIZE, trimmedSearchQuery, 'short-drama', false, false),
  })

  const quickVideoCountQuery = useQuery({
    queryKey: queryKeys.aiChatWorkspaceProjects.count(trimmedSearchQuery),
    enabled: !!session && shortDramaAccessEnabled && activeProjectKind !== 'quick-video' && secondaryCountsReady,
    staleTime: 60_000,
    queryFn: () => fetchAiChatWorkspaceProjects(1, 1, trimmedSearchQuery, false, true),
  })
  const quickVideoCountFromSummary = quickVideoCountQuery.data?.pagination.total

  const aiChatProjectsQuery = useQuery({
    queryKey: queryKeys.aiChatWorkspaceProjects.projects(currentPage, PAGE_SIZE, trimmedSearchQuery, aiChatPreviewsReady),
    enabled: !!session
      && activeProjectKind === 'quick-video',
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: () => fetchAiChatWorkspaceProjects(currentPage, PAGE_SIZE, trimmedSearchQuery, aiChatPreviewsReady),
  })

  const imageTextProjectsQuery = useQuery({
    queryKey: queryKeys.imageText.projects(imageTextPage, IMAGE_TEXT_PAGE_SIZE, trimmedSearchQuery),
    enabled: !!session && activeProjectKind === 'image-text',
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: () => fetchImageTextProjects(imageTextPage, IMAGE_TEXT_PAGE_SIZE, trimmedSearchQuery),
  })

  const projects = projectListQuery.data?.projects ?? EMPTY_PROJECTS
  const pagination: Pagination = projectListQuery.data?.pagination || {
    page: currentPage,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  }
  const imageTextProjects = imageTextProjectsQuery.data?.projects ?? []
  const aiChatProjects = aiChatProjectsQuery.data?.projects ?? []
  const imageTextPagination: Pagination = imageTextProjectsQuery.data?.pagination || {
    page: imageTextPage,
    pageSize: IMAGE_TEXT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  }
  const aiChatPagination: Pagination = aiChatProjectsQuery.data?.pagination || {
    page: currentPage,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  }
  const loading = projectListQuery.isLoading
  const shortDramaCountQuery = useQuery({
    queryKey: queryKeys.workspaceProjects.list(1, 1, trimmedSearchQuery, 'short-drama'),
    enabled: !!session && shortDramaAccessEnabled && activeProjectKind !== 'short-drama' && secondaryCountsReady,
    staleTime: 60_000,
    queryFn: () => fetchWorkspaceProjects(1, 1, trimmedSearchQuery, 'short-drama', true),
  })
  const imageTextCountQuery = useQuery({
    queryKey: queryKeys.imageText.projects(1, 1, trimmedSearchQuery),
    enabled: false,
    staleTime: 60_000,
    queryFn: () => fetchImageTextProjects(1, 1, trimmedSearchQuery, true),
  })
  const shortDramaCount = activeProjectKind === 'short-drama'
    ? pagination.total
    : shortDramaCountQuery.data?.pagination.total ?? 0
  const quickVideoCount = activeProjectKind === 'quick-video'
    ? aiChatProjectsQuery.data?.pagination.total ?? quickVideoCountFromSummary ?? 0
    : quickVideoCountFromSummary ?? 0
  const imageTextCount = activeProjectKind === 'image-text'
    ? imageTextPagination.total
    : imageTextCountQuery.data?.pagination.total ?? 0
  const activePagination = activeProjectKind === 'image-text'
    ? imageTextPagination
    : activeProjectKind === 'quick-video'
      ? aiChatPagination
      : pagination
  const activeLoading = activeProjectKind === 'image-text'
    ? imageTextProjectsQuery.isLoading
    : activeProjectKind === 'quick-video'
      ? aiChatProjectsQuery.isLoading
      : loading

  useEffect(() => {
    if (!session || secondaryCountsReady || activeLoading) return
    return scheduleWorkspaceIdleTask(
      () => setSecondaryCountsReady(true),
      WORKSPACE_SECONDARY_COUNTS_IDLE_TIMEOUT_MS,
    )
  }, [activeLoading, secondaryCountsReady, session])

  useEffect(() => {
    if (
      !session
      || activeProjectKind !== 'quick-video'
      || aiChatPreviewsReady
      || aiChatProjectsQuery.isFetching
      || !aiChatProjectsQuery.data
    ) {
      return
    }
    return scheduleWorkspaceIdleTask(
      () => setAiChatPreviewsReady(true),
      WORKSPACE_AI_CHAT_PREVIEW_IDLE_TIMEOUT_MS,
    )
  }, [
    activeProjectKind,
    aiChatPreviewsReady,
    aiChatProjectsQuery.data,
    aiChatProjectsQuery.isFetching,
    session,
  ])

  const projectsGridClassName = activeProjectKind === 'quick-video'
    ? 'grid grid-cols-1 justify-items-center gap-4 lg:grid-cols-2 lg:justify-items-stretch lg:gap-x-5 lg:gap-y-5'
    : 'grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 md:grid-cols-3 xl:grid-cols-4'
  const showingAssetHub = isAdmin && activeProjectKind === 'short-drama' && activeSecondarySection === 'asset-hub'

  // 检查用户是否已登录
  useEffect(() => {
    if (status === 'loading') return
    // SSO 登录进行中，等待 SSO 完成，不跳转登录页
    if (typeof window !== 'undefined' && window.__ssoLoginPending) return
    if (!session) {
      router.replace({ pathname: '/', query: { auth: 'signin' } })
      return
    }
  }, [session, status, router])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const readProjectId = (kind: ProjectKind) => {
      const sessionHref = window.sessionStorage.getItem(getLastWorkspaceTypeSessionHrefKey(kind))
      const savedHref = userId ? window.localStorage.getItem(getLastWorkspaceTypeHrefKey(userId, kind)) : null
      return getProjectIdFromWorkspaceHref(sessionHref || savedHref)
    }

    const updateCurrentProjectIds = () => {
      setCurrentWorkspaceProjectIds({
        'short-drama': readProjectId('short-drama'),
        'quick-video': readProjectId('quick-video'),
        'image-text': readProjectId('image-text'),
      })
    }

    updateCurrentProjectIds()
    window.addEventListener(WORKSPACE_HREF_CHANGED_EVENT, updateCurrentProjectIds)
    window.addEventListener('storage', updateCurrentProjectIds)
    return () => {
      window.removeEventListener(WORKSPACE_HREF_CHANGED_EVENT, updateCurrentProjectIds)
      window.removeEventListener('storage', updateCurrentProjectIds)
    }
  }, [userId])

  useEffect(() => {
    if (!isBatchSelectMode) {
      setSelectedProjectIds((prev) => (prev.size === 0 ? prev : new Set()))
      return
    }

    const visibleProjectIds = new Set(
      activeProjectKind === 'quick-video'
        ? aiChatProjects.map((project) => project.id)
        : activeProjectKind === 'image-text'
          ? imageTextProjects.map((project) => project.projectId)
          : projects.map((project) => project.id)
    )
    setSelectedProjectIds((prev) => {
      if (prev.size === 0) return prev

      let changed = false
      const next = new Set<string>()
      prev.forEach((id) => {
        if (visibleProjectIds.has(id)) {
          next.add(id)
        } else {
          changed = true
        }
      })

      return changed ? next : prev
    })
  }, [activeProjectKind, aiChatProjects, imageTextProjects, isBatchSelectMode, projects])

  useEffect(() => {
    if (
      typeof window === 'undefined'
      || !userId
      || activeProjectKind !== 'short-drama'
      || loading
      || searchQuery.trim()
    ) return

    const storageKey = getLastWorkspaceHrefKey(userId)
    const savedHref = window.sessionStorage.getItem(LAST_WORKSPACE_SESSION_HREF_KEY) || window.localStorage.getItem(storageKey)
    const savedProjectKind = getProjectKindFromWorkspaceHref(savedHref)

    if (savedProjectKind && savedProjectKind !== activeProjectKind) {
      return
    }

    if (projects.length === 0) {
      window.localStorage.removeItem(storageKey)
      emitWorkspaceHrefChanged(null)
      return
    }

    const savedProjectId = getProjectIdFromWorkspaceHref(savedHref)

    if (savedProjectId && projects.some((project) => project.id === savedProjectId)) {
      return
    }

    const nextHref = buildProjectWorkspaceHref(projects[0])
    if (window.localStorage.getItem(storageKey) !== nextHref) window.localStorage.setItem(storageKey, nextHref)
    const sessionHrefKey = getLastWorkspaceTypeSessionHrefKey(activeProjectKind)
    if (window.sessionStorage.getItem(sessionHrefKey) !== nextHref) window.sessionStorage.setItem(sessionHrefKey, nextHref)
    const typeHrefKey = getLastWorkspaceTypeHrefKey(userId, activeProjectKind)
    if (window.localStorage.getItem(typeHrefKey) !== nextHref) window.localStorage.setItem(typeHrefKey, nextHref)
    setCurrentWorkspaceProjectIds((prev) => ({ ...prev, [activeProjectKind]: projects[0].id }))
    emitWorkspaceHrefChanged(nextHref)
  }, [activeProjectKind, loading, projects, searchQuery, userId])

  // 搜索处理
  const handleSearch = () => {
    setSearchQuery(searchInput)
    setCurrentPage(1)
    setImageTextPage(1)
  }

  const toggleProjectSelection = useCallback((projectId: string) => {
    if (batchDeleting) return
    setSelectedProjectIds((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) {
        next.delete(projectId)
      } else {
        next.add(projectId)
      }
      return next
    })
  }, [batchDeleting])

  const handleBatchDeleteAction = () => {
    if (!isBatchSelectMode) {
      setIsBatchSelectMode(true)
      return
    }
    if (selectedProjectIds.size <= 0) {
      alert(t('batchDeleteNoSelection'))
      return
    }
    setShowBatchDeleteConfirm(true)
  }

  const handleBatchDeleteProjects = async () => {
    const targetIds = Array.from(selectedProjectIds)
    if (targetIds.length === 0) return

    setShowBatchDeleteConfirm(false)
    setBatchDeleting(true)
    setBatchDeleteProgress({ completed: 0, total: targetIds.length })

    try {
      const failedIds: string[] = []
      for (let index = 0; index < targetIds.length; index += 1) {
        const id = targetIds[index]
        try {
          if (activeProjectKind === 'quick-video') {
            const group = aiChatProjects.find((project) => project.id === id)
            if (!group) {
              failedIds.push(id)
            } else {
              let groupFailed = false
              for (const item of group.items) {
                const deleteEndpoint = buildAiChatWorkspaceDeleteEndpoint(item)
                const response = await apiFetch(deleteEndpoint, { method: 'DELETE' })
                if (!isDeleteSuccessResponse(response)) groupFailed = true
              }
              if (groupFailed) failedIds.push(id)
            }
          } else {
            const deleteEndpoint = activeProjectKind === 'image-text'
              ? `/api/image-text/projects/${id}`
              : `/api/projects/${id}`
            const response = await apiFetch(deleteEndpoint, { method: 'DELETE' })
            if (!isDeleteSuccessResponse(response)) {
              failedIds.push(id)
            }
          }
        } catch {
          failedIds.push(id)
        } finally {
          setBatchDeleteProgress({ completed: index + 1, total: targetIds.length })
        }
      }

      const failedCount = failedIds.length
      if (failedCount === targetIds.length) {
        alert(t('batchDeleteAllFailed'))
        setSelectedProjectIds(new Set(failedIds))
      } else if (failedCount > 0) {
        alert(t('batchDeleteFailedPartial', { count: failedCount }))
        setSelectedProjectIds(new Set(failedIds))
      } else {
        setSelectedProjectIds(new Set())
        setIsBatchSelectMode(false)
      }

      void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceProjects.all() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.aiChatWorkspaceProjects.all() })
      void queryClient.invalidateQueries({ queryKey: ['image-text', 'projects'] })
    } finally {
      setBatchDeleting(false)
      setBatchDeleteProgress(null)
    }
  }

  const loadUserPreferencePayload = useCallback(async () => {
    if (userPreferencePayloadRef.current !== undefined) return userPreferencePayloadRef.current
    const res = await apiFetch('/api/user-preference')
    if (!res.ok) throw new Error(`user preference request failed: ${res.status}`)
    const payload: unknown = await res.json()
    userPreferencePayloadRef.current = payload
    return payload
  }, [])

  // 打开新建项目弹窗并检测模型配置
  const openCreateModal = useCallback(() => {
    setShowCreateModal(true)
    // 异步检测模型配置状态
    void (async () => {
      try {
        const payload = await loadUserPreferencePayload()
        setModelNotConfigured(shouldGuideToModelSetup(payload))
      } catch {
        // 忽略检测失败
      }
    })()
  }, [loadUserPreferencePayload])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleOpenCreateProject = () => {
      openCreateModal()
    }

    window.addEventListener(WORKSPACE_CREATE_PROJECT_EVENT, handleOpenCreateProject)
    return () => {
      window.removeEventListener(WORKSPACE_CREATE_PROJECT_EVENT, handleOpenCreateProject)
    }
  }, [openCreateModal])

  useEffect(() => {
    const shouldAutoOpenCreateModal = searchParams?.get('create') === '1'

    if (!shouldAutoOpenCreateModal) {
      createIntentHandledRef.current = false
      return
    }

    if (status !== 'authenticated' || !session || createIntentHandledRef.current) return

    createIntentHandledRef.current = true
    openCreateModal()

    if (typeof window !== 'undefined') {
      const nextUrl = new URL(window.location.href)
      nextUrl.searchParams.delete('create')
      const normalizedUrl = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
      window.history.replaceState(window.history.state, '', normalizedUrl)
    }
  }, [openCreateModal, searchParams, session, status])

  useEffect(() => {
    const shouldGuideCreate = status === 'authenticated'
      && !!session
      && activeProjectKind === 'short-drama'
      && shortDramaAccessEnabled
      && !loading
      && !searchQuery.trim()
      && projects.length === 0

    if (!shouldGuideCreate) {
      emptyWorkspacePromptHandledRef.current = false
      return
    }

    if (emptyWorkspacePromptHandledRef.current || showCreateModal) return

    emptyWorkspacePromptHandledRef.current = true
    openCreateModal()
  }, [activeProjectKind, loading, openCreateModal, projects.length, searchQuery, session, shortDramaAccessEnabled, showCreateModal, status])

  // 分页处理
  const handlePageChange = (newPage: number) => {
    if (activeProjectKind === 'image-text') {
      setImageTextPage(newPage)
      return
    }
    setCurrentPage(newPage)
  }

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    setCreateLoading(true)
    try {
      const response = await apiFetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          mode: 'novel-promotion', // 固定为 novel-promotion
          projectKind: 'short-drama',
        })
      })

      if (response.ok) {
        const createdPayload = await response.json() as {
          project?: Project
          defaultEpisode?: { id?: string | null } | null
        }
        let shouldOpenModelSetup = true
        try {
          const preferencePayload = await loadUserPreferencePayload()
          shouldOpenModelSetup = shouldGuideToModelSetup(preferencePayload)
        } catch (preferenceError) {
          _ulogError('获取用户偏好失败:', preferenceError)
        }

        // 创建成功后刷新第一页
        setSearchQuery('')
        setSearchInput('')
        setCurrentPage(1)
        void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceProjects.all() })
        setShowCreateModal(false)
        setFormData({ name: '', description: '' })

        if (shouldOpenModelSetup) {
          alert(t('analysisModelRequiredAfterCreate'))
          router.push({ pathname: '/profile' })
        }
      } else {
        alert(t('createFailed'))
      }
    } catch (error) {
      _ulogError('创建项目失败:', error)
      alert(t('createFailed'))
    } finally {
      setCreateLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return dateString
    // 转换为北京时间 (UTC+8)
    const beijingTime = new Date(date.getTime() + 8 * 60 * 60 * 1000)
    return WORKSPACE_DATE_FORMATTER.format(beijingTime)
  }

  const handleEditProject = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingProject || !editFormData.name.trim()) return

    setCreateLoading(true)
    try {
      const response = await apiFetch(`/api/projects/${editingProject.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editFormData)
      })

      if (response.ok) {
        const { project: updatedProject } = await response.json() as { project: Project }
        queryClient.setQueriesData<WorkspaceProjectsResponse>(
          { queryKey: queryKeys.workspaceProjects.all() },
          (previous) => {
            if (!previous) return previous
            return {
              ...previous,
              projects: previous.projects.map((project) =>
                project.id === updatedProject.id
                  ? {
                    ...project,
                    name: updatedProject.name,
                    description: updatedProject.description,
                    updatedAt: updatedProject.updatedAt,
                  }
                  : project
              ),
            }
          }
        )
        queryClient.setQueryData(queryKeys.projectData(updatedProject.id), (previous: unknown) => {
          if (!previous || typeof previous !== 'object') return previous
          return {
            ...(previous as Record<string, unknown>),
            name: updatedProject.name,
            description: updatedProject.description,
            updatedAt: updatedProject.updatedAt,
          }
        })
        void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceProjects.all() })
        void queryClient.invalidateQueries({ queryKey: queryKeys.projectData(updatedProject.id) })
        setShowEditModal(false)
        setEditingProject(null)
        setEditFormData({ name: '', description: '' })
      } else {
        alert(t('updateFailed'))
      }
    } catch {
      alert(t('updateFailed'))
    } finally {
      setCreateLoading(false)
    }
  }

  const handleDeleteProject = async () => {
    if (!projectToDelete) return

    setDeletingProjectId(projectToDelete.id)
    setShowDeleteConfirm(false)

    try {
      const responses = projectToDelete.kind === 'ai-chat-works'
        ? await Promise.all((projectToDelete.items || []).map((item) => (
          apiFetch(
            buildAiChatWorkspaceDeleteEndpoint(item),
            { method: 'DELETE' },
          )
        )))
        : [await apiFetch(
          projectToDelete.kind === 'image-text'
            ? `/api/image-text/projects/${projectToDelete.id}`
            : `/api/projects/${projectToDelete.id}`,
          { method: 'DELETE' },
        )]

      if (responses.every(isDeleteSuccessResponse)) {
        // 删除成功后重新获取当前页
        void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceProjects.all() })
        void queryClient.invalidateQueries({ queryKey: queryKeys.aiChatWorkspaceProjects.all() })
        void queryClient.invalidateQueries({ queryKey: ['image-text', 'projects'] })
      } else {
        alert(t('deleteFailed'))
      }
    } catch {
      alert(t('deleteFailed'))
    } finally {
      setDeletingProjectId(null)
      setProjectToDelete(null)
    }
  }

  const openDeleteConfirm = (project: DeleteProjectTarget, e: React.MouseEvent) => {
    e.preventDefault()  // 阻止 Link 导航
    e.stopPropagation()
    setProjectToDelete(project)
    setShowDeleteConfirm(true)
  }

  const cancelDelete = () => {
    setShowDeleteConfirm(false)
    setProjectToDelete(null)
  }
  const batchDeleteButtonText = batchDeleting
    ? t('batchDeletingProgress', {
      done: batchDeleteProgress?.completed ?? 0,
      total: batchDeleteProgress?.total ?? selectedProjectIds.size,
    })
    : isBatchSelectMode
      ? t('batchDeleteSelected', { count: selectedProjectIds.size })
      : t('batchDeleteButton')

  const openEditModal = (project: Project, e: React.MouseEvent) => {
    e.preventDefault()  // 阻止 Link 导航
    e.stopPropagation()
    setEditingProject(project)
    setEditFormData({
      name: project.name,
      description: project.description || ''
    })
    setShowEditModal(true)
  }

  if (status === 'loading') {
    return (
      <div className="glass-page workspace-performance-page min-h-screen flex items-center justify-center" data-glass-preset="subtle">
        <div className="text-[var(--glass-text-secondary)]">{tc('loading')}</div>
      </div>
    )
  }

  if (!session) return null

  return (
    <div className="glass-page workspace-performance-page min-h-screen" data-glass-preset="subtle">
      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 py-5 pb-24 sm:px-6 sm:py-8 lg:px-10">
        <div className="mb-5 flex flex-col gap-4 sm:mb-8 sm:gap-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="mb-1 text-[1.65rem] font-bold leading-tight text-[var(--glass-text-primary)] sm:mb-2 sm:text-3xl">
              {activeProjectKind === 'quick-video' ? 'AI对话作品' : t('title')}
            </h1>
            <p className="text-sm leading-relaxed text-[var(--glass-text-secondary)] sm:text-base">
              {activeProjectKind === 'quick-video'
                ? '图文和短视频按对话归档，删除对话不影响已生成媒体。'
                : t('subtitle')}
            </p>
            {isBatchSelectMode && (
              <p className="mt-2 text-xs leading-relaxed text-[var(--glass-text-tertiary)] sm:text-sm">
                {t('batchSelectHint')}
              </p>
            )}
            <div className="mt-3 sm:mt-4">
              <WorkspaceProjectKindTabs
                activeKind={activeProjectKind}
                counts={{
                  'short-drama': shortDramaCount,
                  'quick-video': quickVideoCount,
                  'image-text': imageTextCount,
                }}
                onChange={setActiveProjectKind}
              />
            </div>
            {shortDramaAccessEnabled && activeProjectKind === 'short-drama' ? (
              <div className="mt-2.5 sm:mt-3">
                <WorkspaceSecondaryNav
                  activeKind={activeProjectKind}
                  activeSection={activeSecondarySection}
                  canShowAssetHub={isAdmin}
                  onChange={setActiveSecondarySection}
                />
              </div>
            ) : null}
          </div>

          {/* 搜索框 */}
          {!showingAssetHub && (
          <div className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] gap-2 sm:flex sm:w-auto">
            <button
              onClick={handleBatchDeleteAction}
              disabled={activeLoading || batchDeleting}
              className={`glass-btn-base shrink-0 whitespace-nowrap px-4 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-50 sm:py-2 ${isBatchSelectMode ? 'glass-btn-tone-danger' : 'glass-btn-secondary'}`}
            >
              {batchDeleteButtonText}
            </button>
            {isBatchSelectMode && (
              <button
                onClick={() => {
                  setIsBatchSelectMode(false)
                  setSelectedProjectIds(new Set())
                }}
                disabled={batchDeleting}
                className="glass-btn-base glass-btn-secondary col-span-3 shrink-0 whitespace-nowrap px-4 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-50 sm:col-span-1 sm:py-2"
              >
                {t('cancelBatchDelete')}
              </button>
            )}
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('searchPlaceholder')}
              className="glass-input-base min-w-0 px-3 py-2.5 text-sm sm:w-64 sm:py-2"
            />
            <button
              onClick={handleSearch}
              className="glass-btn-base glass-btn-primary shrink-0 whitespace-nowrap px-4 py-2.5 text-sm sm:py-2"
            >
              {t('searchButton')}
            </button>
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchInput('')
                  setSearchQuery('')
                  setCurrentPage(1)
                  setImageTextPage(1)
                }}
                className="glass-btn-base glass-btn-secondary col-span-3 shrink-0 whitespace-nowrap px-4 py-2.5 text-sm sm:col-span-1 sm:py-2"
              >
              {t('clearButton')}
              </button>
            )}
          </div>
          )}
          </div>
        </div>

        {showingAssetHub ? (
          <AssetHubContent embedded />
        ) : (
        <>
        {/* Projects Grid */}
        <div className={projectsGridClassName}>
          {/* New Project Card */}
          {activeProjectKind === 'short-drama' ? (
            <div
              onClick={() => openCreateModal()}
	              className="glass-surface flex min-h-[112px] cursor-pointer items-center justify-center p-5 hover:border-[var(--glass-stroke-focus)] sm:min-h-[132px] sm:p-6"
            >
              <div className="flex flex-col items-center gap-2.5 sm:gap-3">
                <div
	                  className="flex h-11 w-11 items-center justify-center rounded-xl sm:h-12 sm:w-12"
                  style={{
                    background: 'linear-gradient(135deg, var(--glass-accent-from) 0%, var(--glass-accent-to) 100%)',
                    boxShadow: '0 14px 30px var(--glass-accent-shadow-soft)',
                  }}
                >
                  <AppIcon name="plus" className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                </div>
	                <span className="text-sm font-medium text-[var(--glass-text-secondary)] group-hover:text-[var(--glass-text-primary)]">
                  {t('newProject')}
                </span>
              </div>
            </div>
          ) : null}

          {/* Project Cards */}
          {activeProjectKind === 'quick-video' ? (
            activeLoading ? (
              Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="glass-surface animate-pulse p-4 sm:p-6">
                  <div className="h-4 bg-[var(--glass-bg-muted)] rounded mb-3"></div>
                  <div className="h-3 bg-[var(--glass-bg-muted)] rounded mb-2"></div>
                  <div className="h-3 bg-[var(--glass-bg-muted)] rounded w-2/3"></div>
                </div>
              ))
            ) : (
              aiChatProjects.map((project, index) => {
                const isSelected = selectedProjectIds.has(project.id)
                const primaryItem = project.items[0] || null
                const previewItem = project.items.find((item) => item.previewUrl === project.previewUrl) || primaryItem
                const projectPreviewMediaKind = getWorkspacePreviewMediaKind(project.previewUrl)
                const isProjectVideoPreview = previewItem?.kind === 'quick-video' && projectPreviewMediaKind === 'video'
                const projectImagePreviewUrl = isProjectVideoPreview ? null : toDisplayImageUrl(project.previewUrl)
                const hasProjectPreview = Boolean(project.previewUrl)
                const projectPreviewRatio = isProjectVideoPreview
                  ? 16 / 9
                  : workspacePreviewRatios[project.id] ?? null
                const cardRatio = clampPreviewRatio(projectPreviewRatio)
                const projectSummary = formatWorkspaceCardSummary(project.summary, 32)
                const recentItems = project.items.slice(0, 2)
                const extraItemCount = Math.max(0, project.items.length - recentItems.length)
                const isRightLeaf = index % 2 === 1
                const leafAlignmentClassName = index % 2 === 0
                  ? 'lg:justify-self-end'
                  : 'lg:justify-self-start'
	                const cardClassName = `${hasProjectPreview
	                  ? 'cursor-pointer relative group block w-fit max-w-full [content-visibility:auto] [contain-intrinsic-size:480px_170px]'
	                  : 'glass-surface cursor-pointer relative group block overflow-hidden [content-visibility:auto] [contain-intrinsic-size:360px_300px]'
                } ${isBatchSelectMode && isSelected
                  ? hasProjectPreview ? '[&_.workspace-preview-info-panel]:border-[var(--glass-tone-danger-fg)]/60' : 'border-[var(--glass-tone-danger-fg)]/60'
                  : hasProjectPreview ? 'hover:[&_.workspace-preview-info-panel]:border-[var(--glass-tone-info-fg)]/40' : 'hover:border-[var(--glass-tone-info-fg)]/40'
                } ${activeProjectKind === 'quick-video' ? leafAlignmentClassName : ''} ${batchDeleting ? 'opacity-80' : ''}`
                const batchSelectControl = isBatchSelectMode ? (
                  <label
                    className={`flex h-7 w-7 items-center justify-center rounded-md border shadow-sm border-[var(--glass-stroke-soft)] bg-white/75 leading-none ${batchDeleting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                    style={{
                      position: 'absolute',
                      top: '0.75rem',
                      right: '0.75rem',
                      left: 'auto',
                      zIndex: 30,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleProjectSelection(project.id)}
                      disabled={batchDeleting}
                      className="h-4 w-4 m-0 accent-red-500 disabled:cursor-not-allowed"
                    />
                  </label>
                ) : null
                const deleteControl = !isBatchSelectMode ? (
                  <button
                    type="button"
                    onClick={(e) => openDeleteConfirm({
                      id: project.id,
                      name: project.name,
                      kind: 'ai-chat-works',
                      items: project.items,
                    }, e)}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-[rgba(239,68,68,0.16)] bg-white/70 text-[var(--glass-tone-danger-fg)] opacity-80 shadow-sm transition hover:border-[rgba(239,68,68,0.34)] hover:bg-white/95 hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-60"
                    title={t('deleteProject')}
                    aria-label={t('deleteProject')}
                    disabled={deletingProjectId === project.id || batchDeleting}
                  >
                    {deletingProjectId === project.id ? (
                      <TaskStatusInline
                        state={resolveTaskPresentationState({
                          phase: 'processing',
                          intent: 'process',
                          resource: 'text',
                          hasOutput: true,
                        })}
                        className="[&>span]:sr-only"
                      />
                    ) : (
                      <AppIcon name="trash" className="h-3.5 w-3.5" />
                    )}
                  </button>
                ) : null
                const cardInner = (
                  hasProjectPreview ? (
                    <div className={`workspace-preview-info-panel glass-surface flex max-w-full items-stretch overflow-hidden p-0 ${isRightLeaf ? 'lg:flex-row-reverse' : ''}`}>
                      <div
                        className={`workspace-preview-card relative ${WORKSPACE_CARD_PREVIEW_HEIGHT_CLASS} shrink-0 overflow-visible`}
                        style={{ aspectRatio: `${cardRatio} / 1` }}
                      >
                        {isProjectVideoPreview && project.previewUrl ? (
                          <WorkspaceProjectPreviewCover src={project.previewUrl} isMirrored={isRightLeaf} />
                        ) : projectImagePreviewUrl ? (
                          <WorkspaceImageRatioCover
                            src={projectImagePreviewUrl}
                            ratio={projectPreviewRatio}
                            isMirrored={isRightLeaf}
                            onRatioChange={(ratio) => rememberWorkspacePreviewRatio(project.id, ratio)}
                          />
                        ) : null}
                        {batchSelectControl}
                        <div className="absolute right-3 top-3 z-30">
                          {deleteControl}
                        </div>
                      </div>
                      <div className={`workspace-preview-copy flex min-w-0 shrink-0 flex-col px-4 py-3 ${WORKSPACE_CARD_COPY_WIDTH_CLASS}`}>
                        <div className="mb-2">
                          <h3 className="line-clamp-1 text-sm font-bold text-[var(--glass-text-primary)] transition-colors group-hover:text-[var(--glass-tone-info-fg)] sm:text-base">
                            {project.name}
                          </h3>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {project.counts.videos > 0 ? (
                            <span className="rounded-full bg-blue-500/10 px-2 py-1 text-[11px] font-bold text-blue-600">
                              {project.counts.videos} 视频
                            </span>
                          ) : null}
                          {project.counts.images > 0 ? (
                            <span className="rounded-full bg-cyan-500/10 px-2 py-1 text-[11px] font-bold text-cyan-700">
                              {project.counts.images} 图片
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-auto pt-2 text-right text-[10px] leading-none text-[var(--glass-text-tertiary)]">
                          {formatDate(project.updatedAt)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {batchSelectControl}
                      <div className="relative z-10 px-4 pb-4 pt-4 sm:px-5 sm:pb-5 sm:pt-5">
                      <div className="mb-2.5 flex items-start justify-between gap-3 sm:mb-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="line-clamp-1 text-base font-bold text-[var(--glass-text-primary)] transition-colors group-hover:text-[var(--glass-tone-info-fg)] sm:text-lg">
                            {project.name}
                          </h3>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {project.counts.videos > 0 ? (
                              <span className="rounded-full bg-blue-500/10 px-2 py-1 text-[11px] font-bold text-blue-600">
                                {project.counts.videos} 视频
                              </span>
                            ) : null}
                            {project.counts.images > 0 ? (
                              <span className="rounded-full bg-cyan-500/10 px-2 py-1 text-[11px] font-bold text-cyan-700">
                                {project.counts.images} 图片
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {deleteControl}
                      </div>
                      {projectSummary && (
                        <div className="mb-3 flex items-start gap-2 sm:mb-4">
                          <AppIcon name="fileText" className="mt-0.5 h-4 w-4 shrink-0 text-[var(--glass-text-tertiary)]" />
                          <p className="truncate text-sm leading-relaxed text-[var(--glass-text-secondary)]">
                            {projectSummary}
                          </p>
                        </div>
                      )}
                      <div className="flex items-center justify-end text-[11px] text-[var(--glass-text-tertiary)]">
                        <span>{formatDate(project.updatedAt)}</span>
                      </div>
                    </div>
                    </>
                  )
                )

                if (isBatchSelectMode || !primaryItem) {
                  return (
                    <div
                      key={project.id}
                      className={cardClassName}
                      data-card-preview-ratio={hasProjectPreview ? cardRatio.toFixed(2) : undefined}
                      style={undefined}
                      onClick={() => {
                        if (batchDeleting) return
                        toggleProjectSelection(project.id)
                      }}
                    >
                      {cardInner}
                    </div>
                  )
                }

            const projectHref = buildAiChatWorkspaceProjectHref(project)

            return (
              <div
                key={project.id}
                role="link"
                tabIndex={0}
                onClick={() => router.push(projectHref)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    router.push(projectHref)
                  }
                }}
                className={cardClassName}
                data-card-preview-ratio={hasProjectPreview ? cardRatio.toFixed(2) : undefined}
                style={undefined}
              >
                {cardInner}
              </div>
            )
              })
            )
          ) : activeProjectKind === 'image-text' ? (
            imageTextProjectsQuery.isLoading ? (
              Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="glass-surface animate-pulse p-4 sm:p-6">
                  <div className="h-4 bg-[var(--glass-bg-muted)] rounded mb-3"></div>
                  <div className="h-3 bg-[var(--glass-bg-muted)] rounded mb-2"></div>
                  <div className="h-3 bg-[var(--glass-bg-muted)] rounded w-2/3"></div>
                </div>
              ))
            ) : (
              imageTextProjects.map((project) => {
                const isSelected = selectedProjectIds.has(project.projectId)
                const previewUrl = toDisplayImageUrl(project.pages.find((page) => page.imageUrl)?.imageUrl) ?? null
                const projectSummary = formatWorkspaceCardSummary(project.description || project.topic)
	                const cardClassName = `glass-surface cursor-pointer relative group block overflow-hidden [content-visibility:auto] [contain-intrinsic-size:360px_300px] ${isBatchSelectMode && isSelected
                  ? 'border-[var(--glass-tone-danger-fg)]/60'
                  : 'hover:border-[var(--glass-tone-info-fg)]/40'
                } ${batchDeleting ? 'opacity-80' : ''}`
                const cardInner = (
                  <>
                    {isBatchSelectMode ? (
                      <label
                        className={`flex h-7 w-7 items-center justify-center rounded-md border shadow-sm border-[var(--glass-stroke-soft)] bg-white/75 leading-none ${batchDeleting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                        style={{
                          position: 'absolute',
                          top: '0.75rem',
                          right: '0.75rem',
                          left: 'auto',
                          zIndex: 30,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleProjectSelection(project.projectId)}
                          disabled={batchDeleting}
                          className="h-4 w-4 m-0 accent-red-500 disabled:cursor-not-allowed"
                        />
                      </label>
                    ) : null}
                    {previewUrl ? (
                      <div className="relative z-10 px-4 pt-4">
                        <WorkspaceImageRatioPreview src={previewUrl} />
                      </div>
                    ) : null}
                    <div className={`relative z-10 p-4 sm:p-5 ${previewUrl ? 'pt-3 sm:pt-4' : ''}`}>
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <h3 className="line-clamp-2 text-base font-bold text-[var(--glass-text-primary)] transition-colors group-hover:text-[var(--glass-tone-info-fg)] sm:text-lg">
                          {project.name}
                        </h3>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <span className="rounded-full bg-[var(--glass-bg-muted)] px-2 py-1 text-[11px] font-semibold text-[var(--glass-text-secondary)]">
                            {tImageText(`status.${project.status}`)}
                          </span>
                          {!isBatchSelectMode ? (
                            <button
                              type="button"
                              onClick={(e) => openDeleteConfirm({
                                id: project.projectId,
                                name: project.name,
                                kind: 'image-text',
                              }, e)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[rgba(239,68,68,0.16)] bg-white/58 text-[var(--glass-tone-danger-fg)] opacity-76 shadow-sm transition hover:border-[rgba(239,68,68,0.34)] hover:bg-white/86 hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-60"
                              title={t('deleteProject')}
                              aria-label={t('deleteProject')}
                              disabled={deletingProjectId === project.projectId || batchDeleting}
                            >
                              {deletingProjectId === project.projectId ? (
                                <TaskStatusInline
                                  state={resolveTaskPresentationState({
                                    phase: 'processing',
                                    intent: 'process',
                                    resource: 'text',
                                    hasOutput: true,
                                  })}
                                  className="[&>span]:sr-only"
                                />
                              ) : (
                                <AppIcon name="trash" className="h-3.5 w-3.5" />
                              )}
                            </button>
                          ) : null}
                        </div>
                      </div>
                      {projectSummary && (
                        <div className="mb-3 flex items-start gap-2 sm:mb-4">
                          <AppIcon name="fileText" className="mt-0.5 h-4 w-4 shrink-0 text-[var(--glass-text-tertiary)]" />
                          <p className="truncate text-sm leading-relaxed text-[var(--glass-text-secondary)]">
                            {projectSummary}
                          </p>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-[11px] text-[var(--glass-text-tertiary)]">
                        <span>{project.pages.length}/{project.pageCount} 张</span>
                        <span>{formatDate(project.updatedAt)}</span>
                      </div>
                    </div>
                  </>
                )

                if (isBatchSelectMode) {
                  return (
                    <div
                      key={project.projectId}
                      className={cardClassName}
                      onClick={() => {
                        if (batchDeleting) return
                        toggleProjectSelection(project.projectId)
                      }}
                    >
                      {cardInner}
                    </div>
                  )
                }

                return (
                  <Link
                    key={project.projectId}
                    href={buildImageTextGalleryHref(project)}
                    className={cardClassName}
                  >
                    {cardInner}
                  </Link>
                )
              })
            )
          ) : loading ? (
            // Loading skeleton
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="glass-surface animate-pulse p-4 sm:p-6">
                <div className="h-4 bg-[var(--glass-bg-muted)] rounded mb-3"></div>
                <div className="h-3 bg-[var(--glass-bg-muted)] rounded mb-2"></div>
                <div className="h-3 bg-[var(--glass-bg-muted)] rounded w-2/3"></div>
              </div>
            ))
          ) : (
            projects.map((project) => {
              const isSelected = selectedProjectIds.has(project.id)
              const isCurrentWorkspaceProject = currentWorkspaceProjectIds[activeProjectKind] === project.id
              const projectPreviewUrl = project.stats?.firstFrameUrl ?? null
              const projectSummary = formatWorkspaceCardSummary(project.description || project.stats?.firstEpisodePreview)
              const projectKindBadgeLabel = tNav('projectWorkspace')
	              const cardClassName = `glass-surface cursor-pointer relative group block overflow-hidden [content-visibility:auto] [contain-intrinsic-size:360px_300px] ${isBatchSelectMode && isSelected
                ? 'border-[var(--glass-tone-danger-fg)]/60'
                : 'hover:border-[var(--glass-tone-info-fg)]/40'
                } ${batchDeleting ? 'opacity-80' : ''}`

              const cardInner = (
                <>
                  {isCurrentWorkspaceProject && (
                    <span className="absolute left-4 top-4 z-30 rounded-full border border-white/55 bg-[linear-gradient(135deg,#3d5cff_0%,#7c5cff_100%)] px-3 py-1 text-xs font-bold text-white shadow-[0_10px_24px_rgba(69,84,189,0.25)]">
                      {projectKindBadgeLabel}
                    </span>
                  )}
                  {isBatchSelectMode && (
                    <label
                      className={`flex h-7 w-7 items-center justify-center rounded-md border shadow-sm border-[var(--glass-stroke-soft)] bg-white/75 leading-none ${batchDeleting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                      style={{
                        position: 'absolute',
                        top: '0.75rem',
                        right: '0.75rem',
                        left: 'auto',
                        zIndex: 30,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleProjectSelection(project.id)}
                        disabled={batchDeleting}
                        className="h-4 w-4 m-0 accent-red-500 disabled:cursor-not-allowed"
                      />
                    </label>
                  )}
                  {/* 悬停光效 */}
	                  {/* 成片预览 */}
                  {projectPreviewUrl ? (
                    <div className={`relative z-10 px-4 ${isCurrentWorkspaceProject ? 'pt-11 sm:pt-12' : 'pt-4'}`}>
                      <WorkspaceProjectPreview src={projectPreviewUrl} />
                    </div>
                  ) : null}

                  <div className={`relative z-10 p-4 sm:p-5 ${projectPreviewUrl ? 'pt-3 sm:pt-4' : isCurrentWorkspaceProject ? 'pt-12 sm:pt-14' : ''}`}>
                    {/* 操作按钮 */}
                    {!isBatchSelectMode && (
                      <div className="absolute right-3 top-3 z-20 flex gap-2 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                        <button
                          onClick={(e) => openEditModal(project, e)}
                          className="glass-btn-base glass-btn-secondary rounded-lg p-2 transition-colors"
                          title={t('editProject')}
                        >
                          <AppIcon name="editSquare" className="w-4 h-4 text-[var(--glass-tone-info-fg)]" />
                        </button>
                        <button
                          onClick={(e) => openDeleteConfirm({
                            id: project.id,
                            name: project.name,
                            kind: activeProjectKind,
                          }, e)}
                          className="glass-btn-base glass-btn-secondary rounded-lg p-2 transition-colors"
                          title={t('deleteProject')}
                          disabled={deletingProjectId === project.id || batchDeleting}
                        >
                          {deletingProjectId === project.id ? (
                            <TaskStatusInline
                              state={resolveTaskPresentationState({
                                phase: 'processing',
                                intent: 'process',
                                resource: 'text',
                                hasOutput: true,
                              })}
                              className="[&>span]:sr-only"
                            />
                          ) : (
                            <AppIcon name="trash" className="w-4 h-4 text-[var(--glass-tone-danger-fg)]" />
                          )}
                        </button>
                      </div>
                    )}

                    {/* 标题 */}
                    <h3 className="mb-2 line-clamp-2 pr-20 text-base font-bold text-[var(--glass-text-primary)] transition-colors group-hover:text-[var(--glass-tone-info-fg)] sm:text-lg">
                      {project.name}
                    </h3>

                    {/* 描述：优先用户描述，fallback 到第一集故事 */}
                    {projectSummary && (
                      <div className="mb-3 flex items-start gap-2 sm:mb-4">
                        <AppIcon name="fileText" className="w-4 h-4 text-[var(--glass-text-tertiary)] mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-[var(--glass-text-secondary)] truncate leading-relaxed">
                          {projectSummary}
                        </p>
                      </div>
                    )}

                    {/* 底部信息 */}
                    <div className="flex items-center justify-between text-[11px] text-[var(--glass-text-tertiary)]">
                      <div className="flex items-center gap-1">
                        <AppIcon name="clock" className="w-3 h-3" />
                        {formatDate(project.updatedAt)}
                      </div>
                      {project.totalCost !== undefined && project.totalCost > 0 && (
                        <span className="text-[11px] font-mono font-medium text-[var(--glass-text-secondary)]">
                          {formatProjectCost(project.totalCost)}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )

              if (isBatchSelectMode) {
                return (
                  <div
                    key={project.id}
                    className={cardClassName}
                    onClick={() => {
                      if (batchDeleting) return
                      toggleProjectSelection(project.id)
                    }}
                  >
                    {cardInner}
                  </div>
                )
              }

              return (
                <Link
                  key={project.id}
                  href={buildProjectWorkspaceHref(project)}
                  className={cardClassName}
                >
                  {cardInner}
                </Link>
              )
            })
          )}
        </div>

        {/* Empty State */}
        {!activeLoading && activeProjectKind === 'short-drama' && projects.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-[var(--glass-bg-muted)] rounded-xl flex items-center justify-center mx-auto mb-4">
              <AppIcon name="folderCards" className="w-8 h-8 text-[var(--glass-text-tertiary)]" />
            </div>
            <h3 className="text-lg font-medium text-[var(--glass-text-primary)] mb-2">
              {searchQuery ? t('noResults') : t('noProjects')}
            </h3>
            <p className="text-[var(--glass-text-secondary)] mb-6">
              {searchQuery ? t('noResultsDesc') : t('noProjectsDesc')}
            </p>
            {!searchQuery && (
              <button
                onClick={() => openCreateModal()}
                className="glass-btn-base glass-btn-primary px-6 py-3"
              >
                {t('newProject')}
              </button>
            )}
          </div>
        )}
        {!activeLoading && activeProjectKind === 'quick-video' && aiChatProjects.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-[var(--glass-bg-muted)] rounded-xl flex items-center justify-center mx-auto mb-4">
              <AppIcon name="video" className="w-8 h-8 text-[var(--glass-text-tertiary)]" />
            </div>
            <h3 className="text-lg font-medium text-[var(--glass-text-primary)] mb-2">
              {searchQuery ? t('noResults') : t('noQuickVideoProjects')}
            </h3>
            <p className="text-[var(--glass-text-secondary)] mb-6">
              {searchQuery ? t('noResultsDesc') : t('noQuickVideoProjectsDesc')}
            </p>
          </div>
        )}
        {!activeLoading && activeProjectKind === 'image-text' && imageTextProjects.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-[var(--glass-bg-muted)] rounded-xl flex items-center justify-center mx-auto mb-4">
              <AppIcon name="imageEdit" className="w-8 h-8 text-[var(--glass-text-tertiary)]" />
            </div>
            <h3 className="text-lg font-medium text-[var(--glass-text-primary)] mb-2">
              {searchQuery ? t('noResults') : t('noImageTextProjects')}
            </h3>
            <p className="text-[var(--glass-text-secondary)] mb-6">
              {searchQuery ? t('noResultsDesc') : t('noImageTextProjectsDesc')}
            </p>
          </div>
        )}
        {/* 分页控件 */}
        {!activeLoading && activePagination.totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              onClick={() => handlePageChange(activePagination.page - 1)}
              disabled={activePagination.page <= 1}
              className="glass-btn-base glass-btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <AppIcon name="chevronLeft" className="w-5 h-5" />
            </button>

            {/* 页码按钮 */}
            {Array.from({ length: activePagination.totalPages }, (_, i) => i + 1)
              .filter(page => {
                // 显示第一页、最后一页、当前页及其前后两页
                return page === 1 ||
                  page === activePagination.totalPages ||
                  Math.abs(page - activePagination.page) <= 2
              })
              .map((page, index, array) => (
                <span key={page} className="flex items-center">
                  {/* 显示省略号 */}
                  {index > 0 && array[index - 1] !== page - 1 && (
                    <span className="px-2 text-[var(--glass-text-tertiary)]">...</span>
                  )}
                  <button
                    onClick={() => handlePageChange(page)}
                    className={`glass-btn-base px-4 py-2 ${page === activePagination.page
                      ? 'glass-btn-primary'
                      : 'glass-btn-secondary'
                      }`}
                  >
                    {page}
                  </button>
                </span>
              ))}

            <button
              onClick={() => handlePageChange(activePagination.page + 1)}
              disabled={activePagination.page >= activePagination.totalPages}
              className="glass-btn-base glass-btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <AppIcon name="chevronRight" className="w-5 h-5" />
            </button>

            <span className="ml-4 text-sm text-[var(--glass-text-tertiary)]">
              {t('totalProjects', { count: activePagination.total })}
            </span>
          </div>
        )}
        </>
        )}
      </main>

      {/* Create Project Modal - 简化版，只有名称和描述 */}
      {showCreateModal && (
        <div className="fixed inset-0 glass-overlay flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="glass-surface-modal p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-[var(--glass-text-primary)] mb-4">{t('createProject')}</h2>
            {modelNotConfigured && (
              <div className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400">
                <AppIcon name="alert" className="w-4 h-4 shrink-0 mt-0.5" />
                <span className="text-[12px] leading-relaxed">
                  {t('modelNotConfigured.before')}
                  <Link
                    href={{ pathname: '/profile' }}
                    className="font-semibold underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-300 mx-0.5"
                    onClick={() => setShowCreateModal(false)}
                  >
                    {t('modelNotConfigured.link')}
                  </Link>
                  {t('modelNotConfigured.after')}
                </span>
              </div>
            )}
            <form onSubmit={handleCreateProject}>
              <div className="mb-4">
                <label htmlFor="name" className="glass-field-label block mb-2">
                  {t('projectName')} *
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="glass-input-base w-full px-3 py-2"
                  placeholder={t('projectNamePlaceholder')}
                  maxLength={100}
                  required
                  autoFocus
                />
              </div>
              <div className="mb-6">
                <label htmlFor="description" className="glass-field-label block mb-2">
                  {t('projectDescription')}
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="glass-textarea-base w-full px-3 py-2"
                  placeholder={t('projectDescriptionPlaceholder')}
                  rows={3}
                  maxLength={500}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setFormData({ name: '', description: '' })
                  }}
                  className="glass-btn-base glass-btn-secondary px-4 py-2"
                  disabled={createLoading}
                >
                  {tc('cancel')}
                </button>
                <button
                  type="submit"
                  className="glass-btn-base glass-btn-primary px-4 py-2 disabled:opacity-50"
                  disabled={createLoading || !formData.name.trim()}
                >
                  {createLoading ? t('creating') : t('createProject')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && editingProject && (
        <div className="fixed inset-0 glass-overlay flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="glass-surface-modal p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-[var(--glass-text-primary)] mb-4">{t('editProject')}</h2>
            <form onSubmit={handleEditProject}>
              <div className="mb-4">
                <label htmlFor="edit-name" className="glass-field-label block mb-2">
                  {t('projectName')} *
                </label>
                <input
                  id="edit-name"
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="glass-input-base w-full px-3 py-2"
                  placeholder={t('projectNamePlaceholder')}
                  maxLength={100}
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="edit-description" className="glass-field-label block mb-2">
                  {t('projectDescription')}
                </label>
                <textarea
                  id="edit-description"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="glass-textarea-base w-full px-3 py-2"
                  placeholder={t('projectDescriptionPlaceholder')}
                  rows={3}
                  maxLength={500}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingProject(null)
                    setEditFormData({ name: '', description: '' })
                  }}
                  className="glass-btn-base glass-btn-secondary px-4 py-2"
                  disabled={createLoading}
                >
                  {tc('cancel')}
                </button>
                <button
                  type="submit"
                  className="glass-btn-base glass-btn-primary px-4 py-2 disabled:opacity-50"
                  disabled={createLoading || !editFormData.name.trim()}
                >
                  {createLoading ? t('saving') : tc('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 删除确认对话框 */}
      <ConfirmDialog
        show={showDeleteConfirm}
        title={t('deleteProject')}
        message={t('deleteConfirm', { name: projectToDelete?.name || '' })}
        confirmText={tc('delete')}
        cancelText={tc('cancel')}
        type="danger"
        onConfirm={handleDeleteProject}
        onCancel={cancelDelete}
      />

      <ConfirmDialog
        show={showBatchDeleteConfirm}
        title={t('batchDeleteButton')}
        message={t('batchDeleteConfirm', { count: selectedProjectIds.size })}
        confirmText={tc('delete')}
        cancelText={tc('cancel')}
        type="danger"
        onConfirm={handleBatchDeleteProjects}
        onCancel={() => setShowBatchDeleteConfirm(false)}
      />
    </div>
  )
}

function WorkspaceSecondaryNav({
  activeKind,
  activeSection,
  canShowAssetHub,
  onChange,
}: {
  activeKind: ProjectKind
  activeSection: WorkspaceSecondarySection
  canShowAssetHub: boolean
  onChange: (section: WorkspaceSecondarySection) => void
}) {
  const t = useTranslations('workspace')
  const tabClassName = (active: boolean) => `inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition ${active
    ? 'bg-white/75 text-[var(--glass-text-primary)] shadow-sm'
    : 'text-[var(--glass-text-secondary)] hover:bg-white/65 hover:text-[var(--glass-text-primary)]'
    }`

  return (
    <nav aria-label={t('secondaryTabs.ariaLabel')} className="flex w-full overflow-x-auto sm:w-fit">
      <div className="inline-flex min-w-fit items-center gap-1.5 rounded-2xl border border-white/50 bg-white/35 p-1 backdrop-blur-[12px]">
        <button
          type="button"
          aria-current={activeSection === 'works' ? 'page' : undefined}
          onClick={() => onChange('works')}
          className={tabClassName(activeSection === 'works')}
        >
          <AppIcon name="folderCards" className={`h-3.5 w-3.5 ${activeSection === 'works' ? 'text-[var(--glass-tone-info-fg)]' : ''}`} />
          <span>{t('secondaryTabs.myWorks')}</span>
        </button>
        {canShowAssetHub && activeKind === 'short-drama' && (
          <button
            type="button"
            aria-current={activeSection === 'asset-hub' ? 'page' : undefined}
            onClick={() => onChange('asset-hub')}
            className={tabClassName(activeSection === 'asset-hub')}
          >
            <AppIcon name="image" className={`h-3.5 w-3.5 ${activeSection === 'asset-hub' ? 'text-[var(--glass-tone-info-fg)]' : ''}`} />
            <span>{t('secondaryTabs.assetHub')}</span>
          </button>
        )}
      </div>
    </nav>
  )
}
